<script>
import snippet from './book-request-ac-snippet'
import objectSansRegular from '../fonts/ObjectSans-Regular.otf'
import objectSansHeavy from '../fonts/ObjectSans-Heavy.otf'
import { BOOK_REQUEST_SUBMISSION_KEY, AMAZON_CONNECT_ARTICLE_ID } from '@constants/book-request'

// App font for the widget. Cloudscape sets font-family on its elements (needs a
// universal !important rule); Regular + Heavy under one family avoid faux-bold.
const FONT_FAMILY =
  "'Object Sans','Nunito',sans-serif,-apple-system,BlinkMacSystemFont,'Segoe UI'"

// CSS the host <head> can't reach: form is in an iframe, controls in shadow roots.
const IFRAME_HEAD_CSS =
  '.connect-customer-interface{box-shadow:none!important;}.evCWiZ{display:none!important;}'
const TEXTBOX_SHADOW_CSS = '.stargate-textbox span{font-weight:inherit!important;}'

// Overrides on the widget's stable classes: full-width centered submit button,
// brand-green deeplink, and no text selection (inputs stay editable).
const UI_OVERRIDES_CSS =
  '.stargate-button-wrapper{width:100%!important;}' +
  '.stargate-button{width:100%!important;justify-content:center!important;text-align:center!important;font-size:1rem!important;}' +
  '.stargate-link,.stargate-link *{color:#29d884!important;}' +
  '*{-webkit-touch-callout:none!important;-webkit-user-select:none!important;-moz-user-select:none!important;-ms-user-select:none!important;user-select:none!important;}' +
  'input,textarea,[contenteditable]{-webkit-user-select:text!important;-moz-user-select:text!important;-ms-user-select:text!important;user-select:text!important;}'

// Validation messages (English -> Spanish). Keys: lowercase, no trailing period.
const VALIDATION_ES = {
  'this field is required': 'Este campo es obligatorio.',
  'enter a valid email': 'Ingresa un correo electrónico válido.',
  'enter a valid telephone number': 'Ingresa un número de teléfono válido.',
}

// Widget submit toasts (English -> the widget's own Spanish). Keys are lowercase.
const TOAST_ES = {
  'thank you. your form was successfully submitted.':
    'Gracias. Su formulario se envió correctamente.',
  'your form was not successfully submitted. you may have to update some information to match expected values. please try again.':
    'Su formulario no se ha enviado correctamente. Es posible que tengas que actualizar algunos datos para que coincidan con los valores esperados. Inténtalo de nuevo.',
}

// Cloudscape aria-label/title/placeholder strings in English.
const UI_ES = {
  search: 'Buscar',
  required: 'obligatorio',
  '(opens new tab)': '(abre en una pestaña nueva)',
  'webform widget': 'Formulario web',
  'date-picker': 'Fecha',
}

export default {
  name: 'BookRequestAcForm',
  mounted() {
    // The widget can't re-init in place; on SPA re-entry (already loaded this
    // session) reload for a clean cold start. External calls are fresh loads.
    if (window.amazon_connect) {
      window.location.reload()
      return
    }
    // EMBEDDED_INLINE renders in place, so inject the snippet into our container.
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.text = snippet
    this.$refs.container.appendChild(script)
    this.styleConnectIframe()
    // Give up and surface an error if the form never loads.
    this.readyTimeout = setTimeout(() => {
      if (!this.isReady) this.$emit('error')
    }, 20000)
  },
  beforeDestroy() {
    clearInterval(this.iframeStyleTimer)
    clearTimeout(this.readyTimeout)
  },
  methods: {
    // The view's hidden FormInputs map to submit Attributes via _linkedFormOutputName;
    // fill each from the deep-link query (article_id from config, the route path is
    // static) so the form carries them on submit. The native value setter + input
    // event make the widget's controlled inputs register the change (no viewConfig
    // edit -> the published view hash stays valid).
    fillHiddenInputs(roots, win) {
      const q = this.$route.query
      const values = {
        // Lowercased on purpose: the Connect flow expects 'pe'. This diverges from the
        // rest of the app (guard, footerLinks, PDF) which uses the uppercase 'PE'
        // canonical — do not "align" it without checking the contact flow.
        country_code: (q.country_code || 'pe').toLowerCase(),
        language: q.language || 'es',
        isguest: q.isguest || 'unknown',
        article_id: AMAZON_CONNECT_ARTICLE_ID,
      }
      roots.forEach(root => {
        Object.keys(values).forEach(name => {
          const value = values[name]
          if (!value) return
          const input = root.querySelector(`input[name="${name}"]`)
          if (!input || input.value === value) return
          const setter = Object.getOwnPropertyDescriptor(win.HTMLInputElement.prototype, 'value').set
          setter.call(input, value)
          input.dispatchEvent(new win.Event('input', { bubbles: true }))
        })
      })
    },
    // Poll: the iframe/form mount async and re-render; id-guarded against re-injection.
    styleConnectIframe() {
      const makeStyle = (doc, id, css) => {
        const style = doc.createElement('style')
        style.id = id
        style.textContent = css
        return style
      }
      const regularUrl = new URL(objectSansRegular, window.location.origin).href
      const heavyUrl = new URL(objectSansHeavy, window.location.origin).href
      const fontFace =
        `@font-face{font-family:'Object Sans';src:local('Object Sans Regular'),url('${regularUrl}') format('opentype');font-weight:normal;}` +
        `@font-face{font-family:'Object Sans';src:local('Object Sans Heavy'),url('${heavyUrl}') format('opentype');font-weight:bold;}`
      const fontRule = `*{font-family:${FONT_FAMILY} !important;}`
      // Translate validation messages by exact text; others left untouched.
      const translateValidation = root => {
        root.querySelectorAll('[class*="awsui_error__message"]').forEach(el => {
          const es = VALIDATION_ES[el.textContent.trim().toLowerCase().replace(/\.$/, '')]
          if (es && el.textContent !== es) el.textContent = es
        })
      }
      // Translate known aria-label/title/placeholder strings by exact match.
      const translateAttrs = root => {
        root.querySelectorAll('[aria-label],[title],[placeholder]').forEach(el => {
          ['aria-label', 'title', 'placeholder'].forEach(attr => {
            const v = el.getAttribute(attr)
            const es = v && UI_ES[v.trim().toLowerCase()]
            if (es && v !== es) el.setAttribute(attr, es)
          })
        })
      }
      // Chrome ignores autocomplete="off" for name/email/tel/address fields and
      // prefills them from the user's profile; a readonly input cannot be prefilled.
      // So set autocomplete off and guard each enabled, named text field as readonly
      // until the user focuses it, then unlock it for typing. Radios and the widget's
      // own search inputs (no name) are left alone.
      const disableAutofill = root => {
        root.querySelectorAll('input,textarea').forEach(el => {
          if (el.getAttribute('autocomplete') !== 'off') el.setAttribute('autocomplete', 'off')
          const type = (el.type || '').toLowerCase()
          if (el.disabled || !el.name || type === 'radio' || type === 'checkbox' || el.dataset.afUnlocked) {
            return
          }
          if (!el.dataset.afGuarded) {
            el.dataset.afGuarded = '1'
            const unlock = () => {
              el.dataset.afUnlocked = '1'
              el.removeAttribute('readonly')
            }
            el.addEventListener('focusin', unlock)
            el.addEventListener('pointerdown', unlock)
          }
          if (!el.hasAttribute('readonly')) el.setAttribute('readonly', 'readonly')
        })
      }
      // Translate the submit toasts; short-circuits unless one is on screen.
      const translateToast = root => {
        if ((root.textContent || '').indexOf('successfully submitted') === -1) return
        root.querySelectorAll('span,p,div').forEach(el => {
          const own = [...el.childNodes]
            .filter(n => n.nodeType === 3)
            .map(n => n.textContent)
            .join('')
            .trim()
          const es = TOAST_ES[own.toLowerCase()]
          if (es && el.textContent !== es) el.textContent = es
        })
      }
      this.iframeStyleTimer = setInterval(() => {
        try {
          const frame = document.querySelector('iframe[id*="amazon-connect"]')
          const doc = frame && frame.contentDocument
          if (!doc) return
          // Inject a <style> once: look it up on `lookupRoot`, append it to `target`
          // (the document head has no getElementById, so the two differ there).
          const ensureStyle = (lookupRoot, target, id, css) => {
            if (!lookupRoot.getElementById(id)) target.appendChild(makeStyle(doc, id, css))
          }

          this.interceptSubmit(frame.contentWindow)

          // Form rendered inside the iframe -> the loader can stop.
          if (!this.isReady && doc.body && doc.body.childElementCount > 0) {
            this.isReady = true
            clearTimeout(this.readyTimeout)
            this.$emit('ready')
          }

          if (doc.head) {
            ensureStyle(doc, doc.head, 'ac-light', IFRAME_HEAD_CSS)
            ensureStyle(doc, doc.head, 'ac-font', fontFace + fontRule)
            ensureStyle(doc, doc.head, 'ac-ui', UI_OVERRIDES_CSS)
          }

          // Translations re-apply every tick (Cloudscape re-renders validation
          // messages and toasts) across the document and every shadow root.
          const shadowRoots = []
          doc.querySelectorAll('*').forEach(el => {
            if (el.shadowRoot) shadowRoots.push(el.shadowRoot)
          })
          this.fillHiddenInputs([doc, ...shadowRoots], frame.contentWindow)
          ;[doc, ...shadowRoots].forEach(root => {
            translateValidation(root)
            translateAttrs(root)
            disableAutofill(root)
            translateToast(root)
          })

          // Fonts/UI overrides live inside each shadow root; TextBox span resets bold.
          shadowRoots.forEach(root => {
            ensureStyle(root, root, 'ac-font', fontRule)
            ensureStyle(root, root, 'ac-ui', UI_OVERRIDES_CSS)
            if (root.querySelector('.stargate-textbox')) {
              ensureStyle(root, root, 'ac-bold', TEXTBOX_SHADOW_CSS)
            }
          })
        } catch (e) {
          clearInterval(this.iframeStyleTimer)
          this.$emit('error')
        }
      }, 500)
    },
    // Wrap the iframe's fetch/XHR once to capture a successful start-email
    // submission. Failures are left to the widget's own error handling so the
    // user doesn't lose the filled form.
    interceptSubmit(win) {
      if (!win || win.acSubmitHooked) return
      win.acSubmitHooked = true
      const self = this
      const isEmail = url => typeof url === 'string' && url.indexOf('/start-email') !== -1
      const origFetch = win.fetch
      win.fetch = function(input, init) {
        const url = typeof input === 'string' ? input : input && input.url
        const body = init && init.body
        return origFetch.apply(this, arguments).then(res => {
          if (isEmail(url) && res.ok) {
            res.clone().json().then(data => self.handleSubmitted(body, data)).catch(() => {})
          }
          return res
        })
      }
      const origSend = win.XMLHttpRequest.prototype.send
      win.XMLHttpRequest.prototype.send = function(body) {
        this.addEventListener('load', () => {
          if (isEmail(this.responseURL) && this.status >= 200 && this.status < 300) {
            try {
              self.handleSubmitted(body, JSON.parse(this.responseText))
            } catch (e) {} // eslint-disable-line no-empty
          }
        })
        return origSend.apply(this, arguments)
      }
    },
    handleSubmitted(reqBody, response) {
      if (this.submitDone || !response || !response.ContactId) return
      this.submitDone = true
      let payload = reqBody
      try {
        if (typeof reqBody === 'string') payload = JSON.parse(reqBody)
      } catch (e) {} // eslint-disable-line no-empty
      const record = {
        payload,
        response,
        contactId: response.ContactId,
        submittedAt: new Date().toISOString(),
      }
      try {
        sessionStorage.setItem(BOOK_REQUEST_SUBMISSION_KEY, JSON.stringify(record))
      } catch (e) {} // eslint-disable-line no-empty
      this.$emit('submitted', record)
    },
  },
}
</script>

<template>
  <div :class="$style.embed">
    <div
      ref="container"
      :class="$style.container"
    />
  </div>
</template>

<style lang="scss" module>
.embed {
  display: flex;
  flex: 1 1 auto;
  width: 100%;
}

.container {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 100%;
}
</style>

<!-- Global overrides for the injected widget DOM (outside Vue's scope). -->
<style>
html,
body {
  height: 100%;
  margin: 0;
}

#amazon-connect-widget-frame {
  max-width: 100% !important;
  height: 100% !important;
}

#amazon-connect-chat-widget {
  height: 100% !important;
}

/* Substring selector survives the build-generated suffix. */
[class*="acFrameContainer"] {
  box-shadow: none !important;
}
</style>
